import React, { useState, useEffect, useRef, useCallback } from 'react';

const SPEEDS = [1, 2, 5, 10, 25];

export default function PlaybackControls({ totalBars, playbackIndex, onIndexChange, bars }) {
  const [playing, setPlaying] = useState(false);
  const [speed, setSpeed] = useState(5);
  const timerRef = useRef(null);
  const indexRef = useRef(playbackIndex);

  // Keep ref in sync
  indexRef.current = playbackIndex;

  const step = useCallback((dir) => {
    const next = Math.max(0, Math.min(totalBars - 1, (indexRef.current ?? totalBars - 1) + dir));
    onIndexChange(next);
  }, [totalBars, onIndexChange]);

  // Auto-play timer
  useEffect(() => {
    if (!playing) {
      if (timerRef.current) clearInterval(timerRef.current);
      return;
    }

    timerRef.current = setInterval(() => {
      const current = indexRef.current ?? 0;
      if (current >= totalBars - 1) {
        setPlaying(false);
        return;
      }
      onIndexChange(current + 1);
    }, Math.max(20, 200 / speed));

    return () => clearInterval(timerRef.current);
  }, [playing, speed, totalBars, onIndexChange]);

  const currentBar = bars && playbackIndex != null ? bars[playbackIndex] : null;
  const dateStr = currentBar?.timestamp
    ? new Date(currentBar.timestamp).toLocaleDateString()
    : playbackIndex != null ? `Bar ${playbackIndex}` : 'All bars';

  const idx = playbackIndex ?? totalBars - 1;

  return (
    <div className="flex flex-col gap-2 px-3 py-2 bg-card/60 border border-border rounded-lg">
      {/* Progress bar row */}
      <div className="flex items-center gap-2">
        <input
          type="range"
          min={0}
          max={totalBars - 1}
          value={idx}
          onChange={(e) => {
            setPlaying(false);
            onIndexChange(parseInt(e.target.value, 10));
          }}
          className="flex-1 h-1.5 accent-primary cursor-pointer"
        />
        <span className="text-xs text-muted-foreground whitespace-nowrap">
          {dateStr}
        </span>
      </div>

      {/* Controls row */}
      <div className="flex items-center justify-center gap-2">
        <button
          onClick={() => step(-1)}
          disabled={idx <= 0}
          className="text-xs px-2 py-1 rounded bg-accent/20 text-foreground hover:bg-accent/40 disabled:opacity-30 transition-colors"
        >
          ◁
        </button>

        <button
          onClick={() => {
            if (playbackIndex == null) onIndexChange(0);
            setPlaying((p) => !p);
          }}
          className="text-xs px-3 py-1 rounded bg-primary/20 text-primary hover:bg-primary/40 font-medium transition-colors"
        >
          {playing ? '⏸' : '▶'}
        </button>

        <button
          onClick={() => step(1)}
          disabled={idx >= totalBars - 1}
          className="text-xs px-2 py-1 rounded bg-accent/20 text-foreground hover:bg-accent/40 disabled:opacity-30 transition-colors"
        >
          ▷
        </button>

        <select
          value={speed}
          onChange={(e) => setSpeed(parseInt(e.target.value, 10))}
          className="text-xs px-1.5 py-1 bg-card border border-border text-foreground rounded focus:outline-none"
        >
          {SPEEDS.map((s) => (
            <option key={s} value={s}>{s}x</option>
          ))}
        </select>

        <button
          onClick={() => {
            setPlaying(false);
            onIndexChange(null);
          }}
          className="text-xs px-2 py-1 rounded bg-accent/20 text-muted-foreground hover:text-foreground hover:bg-accent/40 transition-colors"
        >
          Reset
        </button>
      </div>
    </div>
  );
}
